var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a, _b;
import { defineAsyncComponent } from 'vue';
import { Datepicker } from '@/components/datepicker';
import { httpService, langService, routerService, toastService } from '@/core';
import { translationUrl } from '@/api/sources';
import { getMeta, onDomReady } from '@/utils/browser';
import PrettyConsole from '@/utils/pretty-console';
import * as Sentry from '@sentry/vue';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { useUpdateStore } from '@/stores/update';
import VueTippy from 'vue-tippy';
import '../resources/sass/style.scss';
import app from '@/app.vue';
import Lang from '@/core/plugins/lang';
import { useSidebarStore } from '@/stores/sidebar';
import { useAuthStore } from '@/stores/auth';
import { featureFlagService } from '@/models/feature-flag';
import { authService } from '@/models/auth';
import { moduleService } from '@/models/module-service';
import { experimentsService } from '@/models/experiments-service.ts';
import ButtonV2 from '@/components/button/button-v2.vue';
const Vue = createApp(app);
const pinia = createPinia();
Vue.use(pinia);
Vue.use(VueTippy, {
    directive: 'tippy',
    component: 's-tippy',
});
if (process.env.SENTRY_ENVIRONMENT !== 'local') {
    Sentry.init({
        Vue,
        dsn: process.env.SENTRY_VUE_DSN,
        environment: process.env.SENTRY_ENVIRONMENT,
        release: (_a = getMeta('app_version')) !== null && _a !== void 0 ? _a : undefined,
        attachProps: false,
        integrations: [Sentry.browserTracingIntegration({ router: routerService._router })],
        ignoreErrors: [
            // Network errors
            'timeout of 0ms exceeded',
            'Network Error',
            'ECONNABORTED',
            // Firefox, some user extension is attempting to modify navigator.userAgent
            'can\'t redefine non-configurable property "userAgent"',
            // Firefox bug + from some extension/library
            'ResizeObserver loop completed with undelivered notifications.',
            // Chrome, caused by Grammarly extension
            'ResizeObserver loop limit exceeded',
            'Non-Error promise rejection captured with keys: [object has no keys]',
            // Webpack chunk load error
            /Loading chunk/u,
            // Crisp error
            /decodeAudioData/u,
            // Just ignore http errors in case some slip past
            /Request failed with status code/u,
            // Vue router
            /Redirected when going from/u,
            // Firefox generic exception, usually race condition on object removed? (cause: tinymce)
            /NS_ERROR_UNEXPECTED/u,
            // Some malformed urls
            /SecurityError: Failed to execute 'replaceState' on 'History'/u,
            // gtag.js errors
            'undefined is not an object (evaluating \'b.container[a]\')',
            'Nc(gtag/js)',
            'Mc(gtag/js)',
            'Lc(gtag/js)',
        ],
        beforeSend(event, hint) {
            const error = hint.originalException;
            // ignore AbortController errors to reduce noise
            if (error && error.message && error.message.includes('canceled')) {
                return null;
            }
            return event;
        },
    });
}
let routeAfterLogin = null;
httpService.setCommonHeaders({
    'x-CSRF-TOKEN': (_b = getMeta('csrf-token')) !== null && _b !== void 0 ? _b : '',
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
});
httpService
    .addResponseInterceptor(response => {
    const max = 240;
    const min = 1;
    const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
    if (response.headers['x-server-version'] && useUpdateStore().version === null) {
        useUpdateStore().setVersion(response.headers['x-server-version']);
    }
    if (response.headers['x-refresh-version'] && useUpdateStore().refresh_version === null) {
        useUpdateStore().setRefreshVersion(response.headers['x-refresh-version']);
    }
    if (response.headers['x-update-notification'] && useUpdateStore().update_notification_version === null) {
        useUpdateStore().setUpdateNotificationVersion(response.headers['x-update-notification']);
    }
    else if (response.headers['x-update-notification'] && useUpdateStore().update_notification_version !== null) {
        if (useUpdateStore().update_notification_version === response.headers['x-update-notification']) {
            return response;
        }
    }
    if (response.headers['x-server-version'] && useUpdateStore().version !== null) {
        if (useUpdateStore().version !== response.headers['x-server-version']) {
            const updateTimer = setInterval(() => {
                useUpdateStore().setMismatch(true);
                clearInterval(updateTimer);
            }, randomNumber * 1000);
        }
    }
    if (response.headers['x-refresh-version'] && useUpdateStore().refresh_version !== null) {
        if (useUpdateStore().refresh_version !== response.headers['x-refresh-version']) {
            const updateTimer = setInterval(() => {
                useUpdateStore().setRefreshVersionMismatch(true);
                clearInterval(updateTimer);
            }, randomNumber * 1000);
        }
    }
    return response;
}, error => Promise.reject(error));
httpService
    .addResponseInterceptor(response => response, (error) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const status = (_a = error.response) === null || _a === void 0 ? void 0 : _a.status;
    if (status === 401 && useAuthStore().isLoggedIn) {
        const to = routerService.current.value;
        if (to.name) {
            routeAfterLogin = {
                name: to.name,
                params: to.params,
                query: to.query,
            };
        }
        yield useAuthStore().clear();
        routerService.push({ name: 'login' });
    }
    else if (status === 403) {
        toastService.error({ text: langService.trans('feedback.forbidden') });
    }
    else if (status === 419) {
        window.location.reload();
    }
    return Promise.reject(error);
}));
httpService.addResponseInterceptor((response) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e, _f;
    // Any response that carries a fresh invoice quota (e.g. invoice store/update/delete) keeps
    // the auth store in sync, so the sidebar updates without a dedicated request.
    if ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.meta) === null || _b === void 0 ? void 0 : _b.invoice_quota) {
        useAuthStore().setInvoiceQuota(response.data.meta.invoice_quota);
    }
    const twoFactorChallenge = (_c = response.data) === null || _c === void 0 ? void 0 : _c.two_factor;
    const contactEmail = (_d = response.data) === null || _d === void 0 ? void 0 : _d.contact_email;
    const contactPhone = (_e = response.data) === null || _e === void 0 ? void 0 : _e.contact_phone;
    const twoFactorType = (_f = response.data) === null || _f === void 0 ? void 0 : _f.two_factor_type;
    if (twoFactorChallenge) {
        useAuthStore().setTwoFactorChallengedEmail(contactEmail);
        useAuthStore().setTwoFactorChallengedPhone(contactPhone);
        useAuthStore().setTwoFactorChallengeType(twoFactorType);
        useAuthStore().setTwoFactorChallengeModalVisible(true);
        const isModalVisible = () => useAuthStore().twoFactorChallengeModalVisible;
        yield new Promise(resolve => {
            (function waitForAuth() {
                if (useAuthStore().twoFactorChallengeCompleted || !isModalVisible()) {
                    useAuthStore().setTwoFactorChallengeModalVisible(false);
                    response.data.two_factor_challenge_completed = true;
                    resolve(null);
                }
                else {
                    setTimeout(waitForAuth, 100);
                }
            })();
        });
    }
    return response;
}), (error) => {
    var _a, _b, _c;
    if (((_c = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.user_verify_required) && featureFlagService.can('verify-trial-user')) {
        useAuthStore().setUserVerifyModalVisible(true);
    }
    return Promise.reject(error);
});
routerService.onError((error) => {
    // Webpack chunk missing error
    if (error.name === 'ChunkLoadError') {
        window.location.reload();
        return;
    }
    throw error;
});
// Add global auth route guard
routerService.beforeEach((routeTo) => {
    var _a, _b, _c, _d, _e, _f;
    const authRequired = routeTo.matched.find((route) => route.meta.auth && route.meta.auth.required);
    const loggedIn = useAuthStore().isLoggedIn;
    if (authRequired) {
        if (loggedIn) {
            if (routeAfterLogin === null) {
                return true;
            }
            const toRoute = routeAfterLogin;
            routeAfterLogin = null;
            return toRoute;
        }
        let destination = null;
        if (routeTo.meta.auth && routeTo.meta.auth.redirectGuest) {
            destination = routeTo.meta.auth.redirectGuest;
        }
        else if ((_b = (_a = authRequired.meta) === null || _a === void 0 ? void 0 : _a.auth) === null || _b === void 0 ? void 0 : _b.redirectGuest) {
            destination = authRequired.meta.auth.redirectGuest;
        }
        if (destination) {
            if (process.env.NODE_ENV !== 'production') {
                PrettyConsole.debug('beforeEach', 'Unauthenticated, redirecting to guest route.', destination);
            }
            return destination;
        }
        if (process.env.NODE_ENV !== 'production') {
            PrettyConsole.debug('beforeEach', 'Unauthenticated but no destination found. Preventing navigation.');
        }
        return false;
    }
    const redirectTo = routeTo.matched.find((route) => { var _a, _b; return (_b = (_a = route.meta) === null || _a === void 0 ? void 0 : _a.auth) === null || _b === void 0 ? void 0 : _b.redirectGuest; });
    if (!authRequired && loggedIn && redirectTo && routeTo.name !== ((_d = (_c = redirectTo.meta) === null || _c === void 0 ? void 0 : _c.auth) === null || _d === void 0 ? void 0 : _d.redirectGuest.name)) {
        return (_f = (_e = redirectTo.meta) === null || _e === void 0 ? void 0 : _e.auth) === null || _f === void 0 ? void 0 : _f.redirectGuest;
    }
    return true;
});
routerService.afterEach(() => {
    if (window.innerWidth < 768) {
        useSidebarStore().updateMobileOpen(false);
    }
});
routerService.beforeEach((routeTo, routeFrom) => {
    const matched = routeTo.matched
        .filter(route => Boolean(route.meta.middleware))
        .map(route => route.meta.middleware)
        .flat();
    if (matched && matched.length) {
        let allow = true;
        for (const mw of matched) {
            const result = mw(routeTo, routeFrom);
            if (result !== undefined) {
                allow = result;
            }
            if (allow !== true) {
                if (allow === false) {
                    if (routeFrom.name === null) {
                        return { name: 'empty' };
                    }
                    return false;
                }
                return allow;
            }
        }
    }
    return true;
});
Vue.component('SButton', require('./components/button').Button);
Vue.component('SButtonV2', ButtonV2);
Vue.component('SError', require('./components/form').Error);
Vue.component('SForm', require('./components/form').Form);
Vue.component('SFormGroup', require('./components/form').FormGroup);
Vue.component('SPopper', require('./components/floating').Floating);
Vue.component('SLoader', require('./components/loader').Loader);
Vue.component('SPlaceholderLoader', require('./components/loader').PlaceholderLoader);
Vue.component('SPagination', require('./components/pagination').Pagination);
Vue.component('SSelect', require('./components/select').Select);
Vue.component('STable', require('./components/table').Table);
Vue.component('SCheckbox', require('./components/checkbox').Checkbox);
Vue.component('SModal', require('./components/modal').Modal);
Vue.component('SDatepicker', Datepicker);
Vue.component('SNumericInput', require('./components/numeric-input').NumericInput);
Vue.component('SNumericButton', require('./components/number-button').NumberButton);
Vue.component('SRichText', defineAsyncComponent(() => import('@/components/editor/tinymce.vue')));
Vue.component('STextarea', require('./components/textarea').TextArea);
Vue.component('SSwitch', require('./components/switch').Switch);
Vue.component('SUiBlock', require('./components/ui-block').UiBlock);
Vue.component('SToggleButton', require('./components/toggle-button').ToggleButton);
Vue.component('SFileSelect', require('./components/file-select').FileSelect);
Vue.component('SFormSetting', require('./components/form').FormSetting);
Vue.use(vue => {
    Object.defineProperty(vue.config.globalProperties, '$user', {
        get: () => useAuthStore().user,
    });
});
Vue.use(vue => {
    Object.defineProperty(vue.config.globalProperties, '$company', {
        get: () => useAuthStore().company,
    });
});
Vue.use(vue => {
    Object.defineProperty(vue.config.globalProperties, '$auth', {
        get: () => authService,
    });
});
Vue.use(vue => {
    Object.defineProperty(vue.config.globalProperties, '$module', {
        get: () => moduleService,
    });
});
Vue.use(vue => {
    Object.defineProperty(vue.config.globalProperties, '$currencySymbol', {
        // useAuthStore()?.company?.currency_symbol needs to refactored to define ts object type's
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        get: () => { var _a, _b; return (_b = (_a = useAuthStore()) === null || _a === void 0 ? void 0 : _a.company) === null || _b === void 0 ? void 0 : _b.currency_symbol; },
    });
});
Vue.use(vue => {
    Object.defineProperty(vue.config.globalProperties, '$feature', {
        get: () => featureFlagService,
    });
});
Vue.use(vue => {
    Object.defineProperty(vue.config.globalProperties, '$experiment', {
        get: () => experimentsService,
    });
});
useAuthStore()
    .boot()
    .then(() => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield httpService.get(translationUrl());
    if (response.data.data.translations) {
        yield langService.loadFromResponse(response.data.data.translations, response.data.data.locale, response.data.data.country); // for local testing
    }
    else {
        yield langService.load(response.data.data.locale, response.data.data.country);
    }
    useAuthStore().setAuthFeatureFlags(response.data.data.ff);
    // Very rare occasions but happens where js executes before the DOM is ready,
    // so the "body" is not yet available for use in layer service.
    onDomReady(() => {
        var _a, _b;
        if (process.env.SENTRY_ENVIRONMENT !== 'local') {
            // useAuthStore().user?.profile?.email needs to refactored to define ts object type's
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            Sentry.setUser({ email: (_b = (_a = useAuthStore().user) === null || _a === void 0 ? void 0 : _a.profile) === null || _b === void 0 ? void 0 : _b.email });
        }
        Vue.use(routerService._router);
        Vue.use(Lang);
        Vue.mount('#app');
    });
}));
